import { shakeElement } from "../../Util/helper";
import { FileUploader } from "./file-uploader";
import 'cropperjs';

export class ImageUploader extends FileUploader {
    constructor(inputElement, options = {}) {
        const defaultSize = options.size || 512;
        const imageOptions = {
            maxFiles: 1,
            allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
            maxSize: 8 * 1024 * 1024, // 8MB
            quality: options.quality || 0.8,
            minWidth: options.minWidth || 1,
            minHeight: options.minHeight || 1,
            width: options.width || defaultSize,
            height: options.height || defaultSize,
            circular: options.circular || false,
            size: defaultSize,
            selectButton: options.selectButton || null,
            uploadButton: options.uploadButton || null,
            uploadUrl: options.uploadUrl || null,
            onFileUploaded: (res) => this.imageUploaded(options.onFileUploaded, res),
            ...options
        };

        super(inputElement, imageOptions);

        [this.options.width, this.options.height] = this.getMinimumSize(this.options.width, this.options.height);

        // NOTE: setupPreviewContainer() already ran (via super()) and populated
        // cropperStage / cropperHost / livePreviewImage / livePreviewPlaceholder.
        // Don't touch those fields here again, or you'll wipe them out.
        this.cropper = null;
        this.cropperSelection = null;
        this.currentObjectUrl = null;
        this.currentFile = null;
        this.currentMimeType = 'image/png';
        this.previewUpdateFrame = null;

        this.onSelectionChange = () => this.schedulePreviewUpdate();
        this.onSelectionActionEnd = () => this.schedulePreviewUpdate();
    }

    setSize(width, height) {
        [this.options.width, this.options.height] = this.getMinimumSize(width, height);
        this.updateSelectionAspectRatio();
        this.schedulePreviewUpdate();
    }

    getMinimumSize(width, height) {
        const scale = Math.max(1, this.options.minWidth / width, this.options.minHeight / height);
        return [Math.ceil(width * scale), Math.ceil(height * scale)];
    }

    setupPreviewContainer() {
        super.setupPreviewContainer();

        this.cropperStage = document.getElementById('uploadCropperStage');
        this.cropperHost = document.getElementById('uploadCropperHost');
        this.livePreviewImage = document.getElementById('preview');
        this.livePreviewPlaceholder = document.getElementById('previewPlaceholder');

        this.updateLivePreviewPlaceholder(true);
        this.updateCropperStageVisibility(false);
    }

    async handleFiles(fileList) {
        const files = Array.from(fileList);
        if (files.length === 0) return;

        const file = files[0];
        if (!this.validateFile(file)) return;

        this.currentFile = file;
        this.currentMimeType = file.type || 'image/png';

        if (this.currentObjectUrl) {
            URL.revokeObjectURL(this.currentObjectUrl);
        }

        this.currentObjectUrl = URL.createObjectURL(file);
        this.showEditor(this.currentObjectUrl);
    }

    showEditor(imageData) {
        this.destroyCropper();

        this.options.selectButton?.classList.add('hidden');
        this.options.uploadButton?.classList.remove('hidden');
        this.updateCropperStageVisibility(true);
        this.updateLivePreviewPlaceholder(true);
        this.cropperHost?.classList.toggle('image-uploader-circular', this.options.circular);

        if (!this.cropperHost) return;

        this.cropperHost.innerHTML = `
            <cropper-canvas background style="display: block; width: 100%; height: 100%;">
                <cropper-image src="${imageData}" alt="Selected image" rotatable scalable skewable translatable></cropper-image>
                <cropper-shade></cropper-shade>
                <cropper-handle action="move" plain></cropper-handle>
                <cropper-selection class="${this.options.circular ? 'circular' : ''}" initial-coverage="0.8" movable resizable zoomable keyboard outlined>
                    <cropper-grid role="grid" covered></cropper-grid>
                    <cropper-crosshair centered></cropper-crosshair>
                    <cropper-handle action="move" theme-color="rgba(255, 255, 255, 0.35)"></cropper-handle>
                    <cropper-handle action="n-resize"></cropper-handle>
                    <cropper-handle action="e-resize"></cropper-handle>
                    <cropper-handle action="s-resize"></cropper-handle>
                    <cropper-handle action="w-resize"></cropper-handle>
                    <cropper-handle action="ne-resize"></cropper-handle>
                    <cropper-handle action="nw-resize"></cropper-handle>
                    <cropper-handle action="se-resize"></cropper-handle>
                    <cropper-handle action="sw-resize"></cropper-handle>
                </cropper-selection>
            </cropper-canvas>
        `;

        this.cropperSelection = this.cropperHost.querySelector('cropper-selection');
        this.cropperSelection?.addEventListener('change', this.onSelectionChange);
        this.cropperSelection?.addEventListener('actionend', this.onSelectionActionEnd);
        this.updateSelectionAspectRatio();
        this.cropperSelection?.$center?.();
        this.cropperSelection?.$render?.();
        this.schedulePreviewUpdate();
    }

    cropImage() {
        return new Promise(async (resolve) => {
            if (!this.cropperSelection) {
                resolve(null);
                return;
            }

            try {
                const canvas = await this.cropperSelection.$toCanvas({
                    width: this.options.width,
                    height: this.options.height
                });

                if (!canvas) {
                    resolve(null);
                    return;
                }

                const mimeType = this.getOutputMimeType();
                const quality = mimeType === 'image/png' ? 1 : (this.options.quality || 0.8);

                canvas.toBlob((blob) => {
                    if (!blob) {
                        resolve(null);
                        return;
                    }

                    const fileName = `cropped-image.${mimeType.split('/')[1]}`;
                    resolve(new File([blob], fileName, { type: mimeType, lastModified: Date.now() }));
                }, mimeType, quality);
            } catch (error) {
                console.error('Error cropping image:', error);
                resolve(null);
            }
        });
    }

    // Crop then delegate to FileUploader's upload() — the "Upload picture"
    // button now performs crop + upload in one step.
    async upload() {
        const croppedFile = await this.cropImage();
        if (!croppedFile) {
            this.options.onError('Failed to crop image before upload');
            return;
        }

        this.files.clear();
        this.files.set(crypto.randomUUID(), croppedFile);

        if (this.options.onCropped) {
            this.options.onCropped(croppedFile);
        }

        return super.upload();
    }

    imageUploaded(callback, res) {
        if (res.error) {
            this.options.onError(res.error);
            if (this.options.uploadButton) {
                shakeElement(this.options.uploadButton);
            }
            return;
        }

        this.resetEditor();
        callback?.(res);
    }

    resetEditor() {
        this.destroyCropper();
        this.updateCropperStageVisibility(false);
        this.options.uploadButton?.classList.add('hidden');
        this.options.selectButton?.classList.remove('hidden');

        if (this.currentObjectUrl) {
            URL.revokeObjectURL(this.currentObjectUrl);
            this.currentObjectUrl = null;
        }
        this.currentFile = null;

        if (this.livePreviewImage) {
            this.livePreviewImage.src = '';
            this.livePreviewImage.classList.add('hidden');
        }
        this.updateLivePreviewPlaceholder(true);
    }

    destroy() {
        this.destroyCropper();
        super.destroy?.();
    }

    zoom(factor) {
        this.cropperSelection?.$zoom?.(factor);
        this.schedulePreviewUpdate();
    }

    resetCrop() {
        if (!this.cropperSelection) return;
        this.cropperSelection.$reset?.();
        this.cropperSelection.$center?.();
        this.schedulePreviewUpdate();
    }

    updateSelectionAspectRatio() {
        if (!this.cropperSelection) return;

        const aspectRatio = this.getAspectRatio();
        this.cropperSelection.aspectRatio = aspectRatio;
        this.cropperSelection.initialAspectRatio = aspectRatio;
        this.cropperSelection.initialCoverage = 0.8;
        this.cropperSelection.$render?.();
    }

    getAspectRatio() {
        if (!this.options.width || !this.options.height) return 1;
        return this.options.width / this.options.height;
    }

    getOutputMimeType() {
        if (this.currentMimeType && this.options.allowedTypes.includes(this.currentMimeType)) {
            return this.currentMimeType;
        }
        if (this.options.allowedTypes.includes('image/png')) return 'image/png';
        if (this.options.allowedTypes.includes('image/webp')) return 'image/webp';
        return 'image/jpeg';
    }

    updateLivePreviewPlaceholder(show) {
        this.livePreviewPlaceholder?.classList.toggle('hidden', !show);
    }

    schedulePreviewUpdate() {
        if (this.previewUpdateFrame) {
            cancelAnimationFrame(this.previewUpdateFrame);
        }
        this.previewUpdateFrame = requestAnimationFrame(() => {
            this.previewUpdateFrame = null;
            this.updateCropPreview();
        });
    }

    async updateCropPreview() {
        if (!this.cropperSelection || !this.livePreviewImage) return;

        try {
            const canvas = await this.cropperSelection.$toCanvas({
                width: this.options.width,
                height: this.options.height
            });
            if (!canvas) return;

            this.updateLivePreviewPlaceholder(false);
            const mimeType = this.getOutputMimeType();
            this.livePreviewImage.src = canvas.toDataURL(mimeType, mimeType === 'image/png' ? undefined : (this.options.quality || 0.8));
            this.livePreviewImage.classList.toggle('image-uploader-circular', this.options.circular);
            this.livePreviewImage.classList.remove('hidden');
        } catch (error) {
            console.error('Error updating crop preview:', error);
        }
    }

    destroyCropper() {
        if (this.previewUpdateFrame) {
            cancelAnimationFrame(this.previewUpdateFrame);
            this.previewUpdateFrame = null;
        }

        if (this.cropperSelection) {
            this.cropperSelection.removeEventListener('change', this.onSelectionChange);
            this.cropperSelection.removeEventListener('actionend', this.onSelectionActionEnd);
            this.cropperSelection = null;
        }

        if (this.cropperHost) {
            this.cropperHost.innerHTML = '';
        }
    }

    updateCropperStageVisibility(visible) {
        this.cropperStage?.classList.toggle('hidden', !visible);
    }
}